// The stake field: what somebody typed, and what it would pay.
//
// Three places ask for an amount — the Place bet alert on Today, the ticket
// screen's own field, and the stake on a carbon copy in History — and each of
// them used to carry its own copy of the same four lines. A rule about money
// kept in three places is a rule that drifts, so it is kept here once.
//
// Nothing in this file reads the store or the DOM: a string and a price in, a
// number or null out, which is why it can be pinned in the node suite.

/** Cents, never floating dust: 10.005 is $10.01 and 0.1 + 0.2 is $0.30. */
const cents = (n) => Math.round(n * 100) / 100;

/**
 * What was typed, as a number of dollars, or null when it is not a positive
 * amount.
 *
 * "$1,250.50" is a stake: the dollar sign, the thousands comma and any spaces
 * are how people write money down, not a reason to refuse the figure. The
 * comma is a SEPARATOR and not a decimal point — "10,50" is ten thousand five
 * hundred here, which is the behaviour every screen has always had.
 *
 * Blank, zero, negative and anything that is not a number at all all answer
 * null. The caller says what to do about it, because the sentence a reader
 * needs differs from screen to screen.
 */
export function parseStake(raw) {
  const text = String(raw ?? '').replace(/[$,\s]/g, '');
  const n = Number(text);
  return text && Number.isFinite(n) && n > 0 ? cents(n) : null;
}

/**
 * What that stake returns at those odds, to the cent — or null when either
 * half of the question is missing. The field's own consequence, printed under
 * it before anything has been placed.
 *
 * The odds have to be a POSITIVE number, not merely a finite one: `Number(null)`
 * is 0, and a void ticket carries no combined odds, so a bare finite check
 * would print "Returns $0.00" under the field instead of printing nothing.
 */
/**
 * A fraction of Kelly as the word a punter reads. A quarter is the default and
 * a half is the only other one the editor offers in whole steps; anything else
 * prints as itself rather than as a made-up fraction.
 */
const KELLY_WORDS = { 0.25: '¼', 0.5: '½', 0.75: '¾', 1: 'Full' };

/** A bankroll in the line above: "$500", and "$512.50" where the cents matter. */
const bankrollText = (n) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;

/** A percentage with no trailing zeroes: "2%", "2.5%". */
const capText = (n) => `${+(n * 100).toFixed(2)}%`;

/**
 * WHERE THE PREFILLED FIGURE CAME FROM, in one small line beside it: "¼ Kelly
 * of $500 · capped at 2%".
 *
 * A stake the app worked out is a stake the punter is owed an account of — the
 * ticket's amount used to be the flat default from Settings, which needed no
 * explaining, and a Kelly stake is a number nobody typed. Empty for a flat
 * ticket, for a stored one that never carried the rule, and where there is no
 * bankroll to have taken a fraction of.
 */
export function kellyStakeLine(multi, bankroll) {
  if (multi?.stakeRule !== 'fractional-kelly') return '';
  const bank = Number(bankroll);
  if (!Number.isFinite(bank) || bank <= 0) return '';
  const fraction = Number(multi.stakeFraction);
  const cap = Number(multi.stakeCapPct);
  if (!Number.isFinite(fraction) || !Number.isFinite(cap)) return '';
  const part = KELLY_WORDS[fraction] ?? `${+(fraction * 100).toFixed(2)}% of`;
  return `${part} Kelly of ${bankrollText(bank)} · capped at ${capText(cap)}`;
}

export function stakeReturns(raw, odds) {
  const amount = parseStake(raw);
  const price = Number(odds);
  if (amount === null || !Number.isFinite(price) || price <= 0) return null;
  return cents(amount * price);
}
